
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, type PropType, ref, watch } from 'vue';
import AppError, { type VuelidateValidation } from '@/components/Error.vue';
import { generateID } from '@/common/utils/helpers';

export type SelectValue = string | number | boolean;
export type SelectOptionValue = SelectValue | null;
export type SelectModelValue = SelectValue | SelectValue[] | null;

export interface SelectOption<T extends SelectOptionValue = SelectOptionValue> {
    value: T;
    label: string;
}

/** @deprecated(sine='2026/10/02') keeping for backward compatibility, use SelectOption instead, then remove */
export type Option = SelectOption;

const DEFAULT_PLACEHOLDER = '-- Select an option --';
const SEARCH_PLACEHOLDER = 'Search...';
const NO_RESULTS_TEXT = 'No results found';
const SEARCH_THRESHOLD = 7;

export default defineComponent({
    name: 'AppSelect',
    components: { AppError },
    props: {
        modelValue: { type: [String, Number, Boolean, Array] as PropType<SelectModelValue>, default: null },
        multiple: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        label: { type: String, default: '' },
        options: { type: Array as PropType<SelectOption[]>, default: new Array<SelectOption>() },
        placeholder: { type: String, default: DEFAULT_PLACEHOLDER },
        validations: { type: Object as PropType<VuelidateValidation>, default: undefined }
    },
    emits: ['update:modelValue', 'change'],

    setup(props, { emit }) {
        const wrapperRef = ref<HTMLElement | null>(null);
        const searchInputRef = ref<HTMLInputElement | null>(null);

        const ID = generateID();
        const LABEL_ID = ID + '-label';
        const OPTIONS_ID = ID + '-options';
        const isOpen = ref(false);
        const search = ref('');
        const internalValues = ref<SelectOptionValue[]>([]);
        const isSelected = (value: SelectOptionValue): boolean => internalValues.value.includes(value);

        const showSearchBox = computed(() => isOpen.value && props.options.length > SEARCH_THRESHOLD);

        const selectedOptions = computed(() =>
            props.options.filter(option => internalValues.value.includes(option.value))
        );

        const filteredOptions = computed(() => {
            const keyword = search.value.trim().toLowerCase();
            if (!keyword) {
                return props.options;
            }

            return props.options.filter(option => option.label.toLowerCase().includes(keyword));
        });

        const normalizeValues = (value: SelectModelValue): SelectOptionValue[] => {
            if (Array.isArray(value)) {
                return [...value];
            }

            if (value === null) {
                return props.multiple ? [] : [null];
            }

            return [value];
        };

        const closeDropdown = (): void => {
            isOpen.value = false;
            search.value = '';
        };

        const toggleDropdown = (): void => {
            if (props.disabled) {
                closeDropdown();
                return;
            }

            isOpen.value ? closeDropdown() : (isOpen.value = true);
        };

        const emitValue = (): void => {
            const value: SelectModelValue = props.multiple
                ? [...internalValues.value]
                : internalValues.value[0] ?? null;

            emit('update:modelValue', value);
            emit('change', value);
            props.validations?.$touch();
        };

        const selectOption = (option: SelectOption): void => {
            if (props.disabled) return;

            if (option.value === null) {
                internalValues.value = [];
                if (!props.multiple) {
                    internalValues.value.push(null);
                    closeDropdown();
                }
                emitValue();
                return;
            }

            const index = internalValues.value.indexOf(option.value);

            if (props.multiple) {
                if (index === -1) {
                    internalValues.value.push(option.value);
                } else {
                    internalValues.value.splice(index, 1);
                }
            } else {
                internalValues.value = [option.value];
                closeDropdown();
            }

            emitValue();
        };

        const removeOption = (value: SelectValue): void => {
            if (!props.multiple || props.disabled) return;

            const index = internalValues.value.indexOf(value);
            if (index === -1) return;

            internalValues.value.splice(index, 1);
            emitValue();
        };

        const handleClickOutside = (event: MouseEvent): void => {
            const target = event.target;
            if (!(target instanceof Node) || !wrapperRef.value) return;

            if (!wrapperRef.value.contains(target)) {
                closeDropdown();
            }
        };

        watch(
            () => props.modelValue,
            value => (internalValues.value = normalizeValues(value)),
            { immediate: true }
        );

        watch(
            () => props.options,
            () => {
                if (!props.options.length) {
                    closeDropdown();
                }
            }
        );

        watch(isOpen, value => {
            if (!value) return;

            void nextTick(() => {
                wrapperRef.value
                    ?.querySelector<HTMLElement>('.custom-option.selected')
                    ?.scrollIntoView({ block: 'nearest' });

                searchInputRef.value?.focus();
            });
        });

        onMounted(() => {
            document.addEventListener('mousedown', handleClickOutside);
        });

        onBeforeUnmount(() => {
            document.removeEventListener('mousedown', handleClickOutside);
        });

        return {
            ID,
            LABEL_ID,
            OPTIONS_ID,
            SEARCH_PLACEHOLDER,
            NO_RESULTS_TEXT,
            wrapperRef,
            searchInputRef,
            isOpen,
            search,
            showSearchBox,
            filteredOptions,
            selectedOptions,
            isSelected,
            toggleDropdown,
            closeDropdown,
            selectOption,
            removeOption
        };
    }
});
